import { useCallback, useRef } from 'react';

import { useTranslation } from 'react-i18next';

import { MetaGamesEventType } from '../dto/metagames.types';
import { useFlag } from '../services/launchDarkly';
import type { CoinType } from '../stores/cointype/CoinTypeStore';
import { isChallengeBundleItem, isFreeSpinsBundleItem, isMetaGameBundleItem } from '../stores/rewards/helpers';
import type { ChallengePayload, FreeSpinsPayload, MetaGameTokenPayload } from '../stores/rewards/types';
import type { AnimationProps, ClaimableReward } from '../stores/ui/types';
import { useStores } from '../stores/useStores';
import { formatValueWithK } from '../utils/helpers';

import { useReBouncer } from './use-rebouncer/useRebouncer';

export const useCoinsAnimation = () => {
  const { t } = useTranslation();
  const {
    uiStore,
    coinTypeStore,
    hammersStore,
    puzzleStore,
    animationStore,
    trailStore,
    plinkoStore,
    minigamesStore,
    userFreeSpinsStore,
    bingoStore,
    rouletteStore,
    challengesStore,
    walletStore,
    metagamesStore,
    userSnappyGiftsStore,
  } = useStores();

  const isGC = coinTypeStore.coinType === 'GC';
  const { reBounce: bounceBalance } = useReBouncer('gc_balance');
  const { reBounce: bounceSwitcher } = useReBouncer('switcher');
  const animationsEnabled = useFlag('enable_coins_animation', true);
  const isNewRewardsAnimationEnabled = useFlag('new_rewards_animation', false);
  const miniGameIndexRef = useRef(0);

  const { disabledSounds } = uiStore;

  const processMetagameAnimation = useCallback(
    (payload: MetaGameTokenPayload, onAnimationEnd: () => void) => {
      const { currencyName, amount, coinType } = payload;

      if (currencyName === MetaGamesEventType.HAMMERS.toString()) {
        hammersStore.startClaimHammersAnimation({
          hammerAmount: amount,
          onAnimationEnd,
        });
      } else if (currencyName === MetaGamesEventType.PUZZLE.toString()) {
        puzzleStore.startClaimPuzzleAnimation({
          puzzleAmount: amount,
          coinType,
          onAnimationEnd,
        });
      } else if (currencyName === MetaGamesEventType.TRAIL.toString()) {
        trailStore.startClaimDiceAnimation({
          diceAmount: amount,
          coinType,
          onAnimationEnd,
        });
      } else if (currencyName === MetaGamesEventType.PLINKO.toString()) {
        plinkoStore.startClaimPlinkoAnimation({
          plinkoAmount: amount,
          coinType,
          onAnimationEnd,
        });
      } else if (currencyName === MetaGamesEventType.BINGO.toString()) {
        bingoStore.startClaimBingoAnimation({
          bingoAmount: amount,
          coinType: coinType === 'GC' ? 'GC' : 'CASH',
          onAnimationEnd,
        });
      } else if (currencyName === MetaGamesEventType.ROULETTE.toString()) {
        rouletteStore.startClaimRouletteAnimation({
          rouletteAmount: amount,
          coinType,
          onAnimationEnd,
        });
      } else {
        onAnimationEnd();
      }
    },
    [bingoStore, hammersStore, plinkoStore, puzzleStore, trailStore, rouletteStore],
  );

  const processMetagameAnimationsSequentially = useCallback(
    (payloads: MetaGameTokenPayload[], onAllAnimationsEnd: () => void) => {
      if (!payloads || payloads.length === 0) {
        onAllAnimationsEnd();
        return;
      }

      let currentIndex = 0;

      const processNext = () => {
        if (currentIndex >= payloads.length) {
          onAllAnimationsEnd();
          return;
        }

        const currentPayload = payloads[currentIndex];
        currentIndex++;

        processMetagameAnimation(currentPayload, processNext);
      };

      processNext();
    },
    [processMetagameAnimation],
  );

  const handleStartMiniGames = useCallback(
    (miniGames: FreeSpinsPayload[], onEnd: () => void) => {
      if (miniGames.length === 0) {
        void onEnd?.();
        return;
      }

      // Reset index if we've gone through all games
      if (miniGameIndexRef.current >= miniGames.length) {
        miniGameIndexRef.current = 0;
        void onEnd?.();
        return;
      }

      const currentMiniGame = miniGames[miniGameIndexRef.current];
      if (!currentMiniGame?.gameIds?.[0] && !currentMiniGame.gameId) {
        miniGameIndexRef.current += 1;
        void handleStartMiniGames(miniGames, onEnd);
        return;
      }

      void minigamesStore.startMinigame(currentMiniGame.gameId || currentMiniGame.gameIds[0], () => {
        miniGameIndexRef.current += 1;
        if (miniGameIndexRef.current < miniGames.length) {
          setTimeout(() => {
            void handleStartMiniGames(miniGames, onEnd);
          }, 0);
        } else {
          if (miniGames.length) {
            void userFreeSpinsStore.fetch();
          }
          // Reset for next time
          miniGameIndexRef.current = 0;
          void onEnd?.();
        }
      });
    },
    [minigamesStore, userFreeSpinsStore],
  );

  const handleStartChallenges = useCallback(
    (challenges: ChallengePayload[], onEnd?: () => void) => {
      if (challenges.length === 0) {
        void onEnd?.();
        return;
      }
      challengesStore.setPendingPostPurchaseCoinType(coinTypeStore.coinType);
      void onEnd?.();
    },
    [challengesStore, coinTypeStore],
  );

  const startAnimation = useCallback(
    (args: {
      gcAmount?: number;
      scAmount?: number;
      metagamesTokensAmount?: number;
      onEnd?: () => void | Promise<void>;
      animationProps?: AnimationProps;
      currencyName?: string;
      coinType?: CoinType;
      metagamesRewardsPayloads?: MetaGameTokenPayload[];
      rewards?: ClaimableReward[];
      refetchData?: boolean;
      enableSounds?: boolean;
      enableNewRewardsAnimation?: boolean;
      startMiniGames?: boolean;
      hideOverlay?: boolean;
    }) => {
      const {
        rewards = [],
        refetchData = true,
        enableSounds: enableSoundsArg = true,
        enableNewRewardsAnimation = true,
        startMiniGames = true,
        hideOverlay: hideOverlayArg,
      } = args;

      const enableSounds = enableSoundsArg && !disabledSounds;

      const gcAmount = rewards.reduce((acc, pkg) => acc + pkg.gcCoin, 0);
      const scAmount = rewards.reduce((acc, pkg) => acc + pkg.scCoin, 0);
      const metagamesRewardsPayloads = rewards
        .flatMap((pkg) => pkg.bundleItems)
        .filter(isMetaGameBundleItem)
        .map((item) => item.bundlePayload);

      const isNewAnimationEnabled = isNewRewardsAnimationEnabled && enableNewRewardsAnimation;
      animationStore.setAnimationBlockScreenVisible(true);

      const onAnimationEnd = () => {
        // Flag is disabled only for automation tests users
        if (!animationsEnabled) {
          void walletStore.fetchWallet();
          void userFreeSpinsStore.fetch();
          void metagamesStore.fetch();
          void userSnappyGiftsStore.fetch();
          void challengesStore.fetch();
        }
        void args.onEnd?.();
        animationStore.setAnimationBlockScreenVisible(false);
      };

      if ((!animationsEnabled || (!gcAmount && !scAmount)) && !isNewAnimationEnabled) {
        if (metagamesRewardsPayloads.length > 0) {
          processMetagameAnimationsSequentially(metagamesRewardsPayloads, onAnimationEnd);
        } else if (args.metagamesTokensAmount && args.currencyName === MetaGamesEventType.HAMMERS.toString()) {
          hammersStore.startClaimHammersAnimation({
            hammerAmount: args.metagamesTokensAmount,
            onAnimationEnd,
          });
        } else if (args.metagamesTokensAmount && args.currencyName === MetaGamesEventType.PUZZLE.toString()) {
          puzzleStore.startClaimPuzzleAnimation({
            puzzleAmount: args.metagamesTokensAmount || 0,
            coinType: args.coinType,
            onAnimationEnd,
          });
        } else if (args.metagamesTokensAmount && args.currencyName === MetaGamesEventType.TRAIL.toString()) {
          trailStore.startClaimDiceAnimation({
            diceAmount: args.metagamesTokensAmount || 0,
            coinType: args.coinType,
            onAnimationEnd,
          });
        } else if (args.metagamesTokensAmount && args.currencyName === MetaGamesEventType.PLINKO.toString()) {
          plinkoStore.startClaimPlinkoAnimation({
            plinkoAmount: args.metagamesTokensAmount || 0,
            coinType: args.coinType,
            onAnimationEnd,
          });
        } else if (args.metagamesTokensAmount && args.currencyName === MetaGamesEventType.BINGO.toString()) {
          bingoStore.startClaimBingoAnimation({
            bingoAmount: args.metagamesTokensAmount || 0,
            coinType: args.coinType === 'SC' ? 'CASH' : 'GC',
            onAnimationEnd,
          });
        } else if (args.metagamesTokensAmount && args.currencyName === MetaGamesEventType.ROULETTE.toString()) {
          rouletteStore.startClaimRouletteAnimation({
            rouletteAmount: args.metagamesTokensAmount || 0,
            coinType: args.coinType,
            onAnimationEnd,
          });
        } else {
          onAnimationEnd();
        }
        return;
      }
      const isScInteger = Number.isInteger(Number(scAmount));
      const nextScValue = isScInteger ? scAmount : Number(scAmount).toFixed(2);

      const processBundleItems = () => {
        const challenges = rewards
          .flatMap((pkg) => pkg.bundleItems)
          .filter(isChallengeBundleItem)
          .map((item) => item.bundlePayload);

        const miniGames = rewards
          .flatMap((pkg) => pkg.bundleItems)
          .filter(isFreeSpinsBundleItem)
          .map((item) => item.bundlePayload)
          .filter((item) => ['Barouletta Gaming', 'Minigames'].includes(item.provider));

        uiStore.setClaimRewardAnimationState({ enable: false });

        if (challenges.length > 0) {
          handleStartChallenges(challenges, onAnimationEnd);
        } else if (miniGames.length > 0) {
          handleStartMiniGames(miniGames, onAnimationEnd);
        } else {
          onAnimationEnd();
        }
      };

      const onEnd = () => {
        if (!isNewAnimationEnabled) {
          if (metagamesRewardsPayloads.length > 0) {
            processMetagameAnimationsSequentially(metagamesRewardsPayloads, processBundleItems);
          } else if (args.metagamesTokensAmount && args.currencyName === MetaGamesEventType.HAMMERS.toString()) {
            hammersStore.startClaimHammersAnimation({
              hammerAmount: args.metagamesTokensAmount,
              onAnimationEnd,
            });
          } else if (args.currencyName === MetaGamesEventType.PUZZLE.toString()) {
            puzzleStore.startClaimPuzzleAnimation({
              puzzleAmount: args.metagamesTokensAmount || 0,
              coinType: args.coinType,
              onAnimationEnd,
            });
          } else if (args.currencyName === MetaGamesEventType.TRAIL.toString()) {
            trailStore.startClaimDiceAnimation({
              diceAmount: args.metagamesTokensAmount || 0,
              coinType: args.coinType,
              onAnimationEnd,
            });
          } else if (args.currencyName === MetaGamesEventType.PLINKO.toString()) {
            plinkoStore.startClaimPlinkoAnimation({
              plinkoAmount: args.metagamesTokensAmount || 0,
              coinType: args.coinType,
              onAnimationEnd,
            });
          } else if (args.currencyName === MetaGamesEventType.BINGO.toString()) {
            bingoStore.startClaimBingoAnimation({
              bingoAmount: args.metagamesTokensAmount || 0,
              coinType: args.coinType === 'SC' ? 'CASH' : 'GC',
              onAnimationEnd,
            });
          } else if (args.currencyName === MetaGamesEventType.ROULETTE.toString()) {
            rouletteStore.startClaimRouletteAnimation({
              rouletteAmount: args.metagamesTokensAmount || 0,
              coinType: args.coinType,
              onAnimationEnd,
            });
          } else {
            onAnimationEnd();
          }
        } else {
          processBundleItems();
        }
      };

      if (isNewAnimationEnabled) {
        uiStore.startClaimRewardAnimation({
          onEnd: onEnd,
          rewards,
          refetchData,
          enableSounds,
          startMiniGames,
          hideOverlay: hideOverlayArg,
        });
      } else {
        uiStore.startCoinsAnimation({
          gcAmount: gcAmount ? formatValueWithK(gcAmount, t) : 0,
          scAmount: nextScValue,
          onGcCoin: isGC ? bounceBalance : bounceSwitcher,
          onScCoin: isGC ? bounceSwitcher : bounceBalance,
          onEnd: onEnd,
          animationProps: args.animationProps,
        });
      }
    },
    [
      animationsEnabled,
      disabledSounds,
      uiStore,
      isGC,
      processMetagameAnimationsSequentially,
      bounceBalance,
      bounceSwitcher,
      animationStore,
      hammersStore,
      puzzleStore,
      trailStore,
      isNewRewardsAnimationEnabled,
      t,
      plinkoStore,
      handleStartMiniGames,
      bingoStore,
      rouletteStore,
      handleStartChallenges,
      walletStore,
      userFreeSpinsStore,
      metagamesStore,
      userSnappyGiftsStore,
      challengesStore,
    ],
  );

  return { startAnimation };
};
